/**
 * Single source of truth for which locales have REAL translated content
 * (not just UI-shell strings).
 *
 * Pages declare which locales they're available in via `localesAvailable`
 * in their data file. The hreflang component + sitemap script intersect
 * that list with this global allowlist before emitting any alternate
 * language link.
 *
 * Multilingual safety:
 *   • Locale-prefixed URLs (e.g. /vi/dental-treatments-in-vietnam) still
 *     resolve in the SPA so the language switcher keeps working — but
 *     they receive <meta name="robots" content="noindex,follow"> until
 *     they have real translated content. This prevents Google from
 *     indexing thin/duplicate language pages.
 *   • Sitemap.xml + hreflang only emit URLs in REAL_CONTENT_LOCALES
 *     intersected with each page's `localesAvailable` list.
 *
 * To "go live" with a new locale on a page:
 *   1. Translate the page content (data file or component).
 *   2. Add the locale code to that page's `localesAvailable` array.
 *   3. Add the locale code here in REAL_CONTENT_LOCALES if it's a new
 *      locale launching for the first time.
 *   4. Rebuild sitemap (`yarn sitemap` or `yarn build`).
 *
 * Regional English variants (en-AU, en-GB, en-US, en-CA) all point at
 * the SAME canonical English URL — no separate URLs, no duplicate content.
 * They're emitted as hreflang alternates only, to help Google serve the
 * right regional variant of the same page.
 */

// Locales with real, human-quality long-form content.
// Currently only English. Expand as proper translations are added per page.
export const REAL_CONTENT_LOCALES = ['en'];

// Regional English hreflang variants. All map to the same English URL.
// Listed in priority order (most important markets first).
export const REGIONAL_EN_HREFLANGS = ['en', 'en-AU', 'en-GB', 'en-US', 'en-CA'];

// All locales the SPA can render (matches LanguageContext.SUPPORTED_CODES).
// Used to detect locale-prefixed URLs for noindex handling.
export const ALL_SPA_LOCALES = ['en', 'vi', 'fr', 'de', 'ar', 'zh', 'ko', 'ja', 'ru'];

/**
 * Returns true if the given locale has real translated content.
 * Pages can pass their own `localesAvailable` to narrow the check further.
 */
export const isRealContentLocale = (locale, pageLocalesAvailable = null) => {
  if (!REAL_CONTENT_LOCALES.includes(locale)) return false;
  if (Array.isArray(pageLocalesAvailable) && pageLocalesAvailable.length > 0) {
    return pageLocalesAvailable.includes(locale);
  }
  return true;
};

/**
 * Returns true if the URL pathname starts with a non-default locale prefix
 * (e.g. /vi/foo, /fr/bar). Used by HreflangTags to decide noindex.
 */
export const hasLocalePrefix = (pathname) => {
  if (!pathname || typeof pathname !== 'string') return false;
  const match = pathname.match(/^\/([a-z]{2})(\/|$)/i);
  if (!match) return false;
  const code = match[1].toLowerCase();
  return ALL_SPA_LOCALES.includes(code) && code !== 'en';
};
